import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { Plus, Trash, PencilSimple, ChartLine, Sparkle, ChartBar } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, LineChart, Line, Legend, Cell } from 'recharts';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { listLandingVariants, createLandingVariant, updateLandingVariant, deleteLandingVariant, analyzeLandingAB } from '../services/api';

const EMPTY = { label: '', weight: 50, is_active: true, snapshot_text: '{}' };

const CHART_COLORS = ['#2D5A4C', '#00AEEF', '#FF8E00', '#D5007D', '#80C51F', '#EA3C3A', '#5E6D66'];

function AnalyticsPanel({ items }) {
  if (!items || items.length === 0) {
    return (
      <Card className="border-[#E8E6E1]"><CardContent className="p-8 text-center text-[#5E6D66]" data-testid="analytics-empty">
        <ChartBar size={32} className="mx-auto mb-2" weight="duotone" />
        Aucune donnée à afficher — créez au moins une variante pour visualiser les analytics.
      </CardContent></Card>
    );
  }

  // Data : cumul par variante
  const chartData = items.map((v) => ({
    name: v.label.length > 18 ? v.label.slice(0, 18) + '…' : v.label,
    impressions: v.impressions || 0,
    conversions: v.conversions || 0,
    taux: v.impressions > 0 ? Number(((v.conversions / v.impressions) * 100).toFixed(2)) : 0,
  }));

  // Data événements agrégés (all events across variants)
  const eventMap = {};
  items.forEach((v) => {
    const ev = v.events || {};
    Object.entries(ev).forEach(([k, count]) => {
      eventMap[k] = (eventMap[k] || 0) + (Number(count) || 0);
    });
  });
  const eventsData = Object.entries(eventMap)
    .map(([event, count]) => ({ event: event.length > 22 ? event.slice(0, 22) + '…' : event, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);

  // Taux global
  const totalImp = items.reduce((a, v) => a + (v.impressions || 0), 0);
  const totalConv = items.reduce((a, v) => a + (v.conversions || 0), 0);
  const globalRate = totalImp > 0 ? ((totalConv / totalImp) * 100).toFixed(2) : '0.00';

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card className="border-[#E8E6E1]"><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wider text-[#5E6D66] font-semibold">Taux global</p>
          <p className="text-3xl font-bold text-[#2D5A4C]" data-testid="analytics-global-rate">{globalRate}%</p>
        </CardContent></Card>
        <Card className="border-[#E8E6E1]"><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wider text-[#5E6D66] font-semibold">Meilleur taux</p>
          <p className="text-2xl font-bold text-emerald-700">{Math.max(...chartData.map(d => d.taux), 0).toFixed(2)}%</p>
        </CardContent></Card>
        <Card className="border-[#E8E6E1]"><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wider text-[#5E6D66] font-semibold">Événements tracés</p>
          <p className="text-2xl font-bold text-[#FF8E00]">{eventsData.length}</p>
        </CardContent></Card>
        <Card className="border-[#E8E6E1]"><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wider text-[#5E6D66] font-semibold">Variantes suivies</p>
          <p className="text-2xl font-bold text-blue-700">{items.length}</p>
        </CardContent></Card>
      </div>

      <Card className="border-[#E8E6E1]" data-testid="analytics-rate-chart">
        <CardContent className="p-5">
          <h3 className="text-sm font-bold text-[#1A2421] mb-3 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <ChartLine size={16} className="text-[#2D5A4C]" weight="duotone" />
            Taux de conversion par variante
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                <XAxis dataKey="name" tick={{ fill: '#5E6D66', fontSize: 11 }} />
                <YAxis tick={{ fill: '#5E6D66', fontSize: 11 }} unit="%" />
                <RTooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E8E6E1', borderRadius: 8 }} formatter={(v) => [`${v}%`, 'Taux']} />
                <Bar dataKey="taux" radius={[6, 6, 0, 0]}>
                  {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="border-[#E8E6E1]" data-testid="analytics-volume-chart">
        <CardContent className="p-5">
          <h3 className="text-sm font-bold text-[#1A2421] mb-3 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <ChartBar size={16} className="text-[#00AEEF]" weight="duotone" />
            Volume impressions vs conversions
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                <XAxis dataKey="name" tick={{ fill: '#5E6D66', fontSize: 11 }} />
                <YAxis tick={{ fill: '#5E6D66', fontSize: 11 }} />
                <RTooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E8E6E1', borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="impressions" fill="#00AEEF" radius={[6, 6, 0, 0]} />
                <Bar dataKey="conversions" fill="#80C51F" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {eventsData.length > 0 && (
        <Card className="border-[#E8E6E1]" data-testid="analytics-events-chart">
          <CardContent className="p-5">
            <h3 className="text-sm font-bold text-[#1A2421] mb-3 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Sparkle size={16} className="text-[#D5007D]" weight="fill" />
              Événements de conversion (cumulés)
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={eventsData} margin={{ top: 5, right: 10, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                  <XAxis type="number" tick={{ fill: '#5E6D66', fontSize: 11 }} />
                  <YAxis type="category" dataKey="event" tick={{ fill: '#5E6D66', fontSize: 10 }} width={140} />
                  <RTooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E8E6E1', borderRadius: 8 }} />
                  <Bar dataKey="count" fill="#D5007D" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


export default function LandingVariantsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const isAdmin = user && ['super_admin', 'admin', 'direction'].includes(user.role);

  const load = async () => {
    setLoading(true);
    try {
      const r = await listLandingVariants();
      setItems(r.data.items || []);
      setSummary(r.data.summary || {});
    } catch { toast.error('Erreur de chargement'); }
    setLoading(false);
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  if (!user) return null;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (v) => { setEditing(v); setForm({ label: v.label, weight: v.weight, is_active: !!v.is_active, snapshot_text: JSON.stringify(v.snapshot || {}, null, 2) }); setDialogOpen(true); };

  const save = async () => {
    if (!form.label) return toast.error('Libellé requis');
    let snapshot;
    try { snapshot = JSON.parse(form.snapshot_text || '{}'); } catch { return toast.error('JSON snapshot invalide'); }
    setSaving(true);
    try {
      const payload = { label: form.label, weight: Number(form.weight), is_active: !!form.is_active, snapshot };
      if (editing) await updateLandingVariant(editing.id, payload);
      else await createLandingVariant(payload);
      toast.success('Variante enregistrée');
      setDialogOpen(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  const remove = async (v) => {
    if (!window.confirm(`Supprimer « ${v.label} » ?`)) return;
    try { await deleteLandingVariant(v.id); toast.success('Supprimée'); await load(); }
    catch { toast.error('Erreur'); }
  };

  const runAnalysis = async (autoCreate) => {
    if (items.length < 2) return toast.error('Il faut au moins 2 variantes pour lancer une analyse');
    setAnalyzing(true);
    try {
      const r = await analyzeLandingAB(autoCreate);
      setAnalysis(r.data);
      setAnalysisOpen(true);
      if (autoCreate && r.data.created_variant) {
        toast.success(`Variante fusionnée « ${r.data.merged_label} » créée (inactive)`);
        await load();
      } else {
        toast.success('Analyse IA terminée');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur analyse IA');
    }
    setAnalyzing(false);
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="landing-ab-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>Landing A/B — Variantes</h1>
          <p className="text-sm text-[#5E6D66]">Testez plusieurs versions de <code className="bg-[#F5F4EF] px-1.5 py-0.5 rounded text-xs">/rejoindre</code>. Poids = probabilité relative d'affichage.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => runAnalysis(false)} variant="outline" disabled={analyzing || items.length < 2} data-testid="variant-analyze-ai">
            {analyzing ? <Loader2 className="animate-spin mr-1" size={14} /> : <Sparkle size={14} className="mr-1" weight="fill" />}
            Analyse IA
          </Button>
          <Button onClick={openNew} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="variant-new">
            <Plus size={14} className="mr-1" /> Nouvelle variante
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="border-[#E8E6E1]"><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wider text-[#5E6D66] font-semibold">Variantes actives</p>
          <p className="text-3xl font-bold text-[#2D5A4C]" data-testid="ab-active-count">{items.filter(i => i.is_active).length}</p>
        </CardContent></Card>
        <Card className="border-[#E8E6E1]"><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wider text-[#5E6D66] font-semibold">Impressions cumulées</p>
          <p className="text-3xl font-bold text-blue-700" data-testid="ab-impressions">{summary.total_impressions || 0}</p>
        </CardContent></Card>
        <Card className="border-[#E8E6E1]"><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wider text-[#5E6D66] font-semibold">Conversions</p>
          <p className="text-3xl font-bold text-emerald-700" data-testid="ab-conversions">{summary.total_conversions || 0}</p>
        </CardContent></Card>
      </div>

      <Tabs defaultValue="list" className="w-full">
        <TabsList className="bg-white border border-[#E8E6E1]">
          <TabsTrigger value="list" data-testid="tab-ab-list"><ChartLine size={14} className="mr-1.5" /> Variantes</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-ab-analytics"><ChartBar size={14} className="mr-1.5" /> Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-4 space-y-3">
          {loading && <p className="text-center text-[#5E6D66]">Chargement…</p>}
          {!loading && items.length === 0 && (
            <Card className="border-[#E8E6E1]"><CardContent className="p-8 text-center text-[#5E6D66]">
              <ChartLine size={32} className="mx-auto mb-2" weight="duotone" />
              Aucune variante active — le contenu par défaut de <code className="bg-[#F5F4EF] px-1 rounded">/rejoindre</code> est servi.
            </CardContent></Card>
          )}
          <div className="space-y-2">
            {items.map((v) => {
              const rate = v.impressions ? ((v.conversions / v.impressions) * 100).toFixed(1) : '0.0';
              return (
                <Card key={v.id} className={`border-[#E8E6E1] ${v.is_active ? '' : 'opacity-60'}`} data-testid={`variant-${v.id}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-[#1A2421] truncate">{v.label}</p>
                        {v.is_active ? <span className="text-[9px] uppercase bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full font-semibold">actif</span>
                                       : <span className="text-[9px] uppercase bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded-full font-semibold">inactif</span>}
                      </div>
                      <p className="text-[10px] text-[#5E6D66]">Poids : {v.weight} — Snapshot : {Object.keys(v.snapshot || {}).length} clé(s)</p>
                    </div>
                    <div className="text-center min-w-[70px]">
                      <p className="text-[9px] uppercase text-[#5E6D66]">Impressions</p>
                      <p className="text-lg font-bold text-blue-700">{v.impressions || 0}</p>
                    </div>
                    <div className="text-center min-w-[70px]">
                      <p className="text-[9px] uppercase text-[#5E6D66]">Conversions</p>
                      <p className="text-lg font-bold text-emerald-700">{v.conversions || 0}</p>
                    </div>
                    <div className="text-center min-w-[60px]">
                      <p className="text-[9px] uppercase text-[#5E6D66]">Taux</p>
                      <p className="text-lg font-bold text-[#2D5A4C]">{rate}%</p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(v)} data-testid={`edit-variant-${v.id}`}><PencilSimple size={14} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(v)} data-testid={`del-variant-${v.id}`}><Trash size={14} className="text-red-600" /></Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="mt-4 space-y-4" data-testid="ab-analytics-panel">
          <AnalyticsPanel items={items} />
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="variant-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Modifier la variante' : 'Nouvelle variante'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Libellé *</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="variant-label" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Poids (0-100)</Label><Input type="number" min={0} max={100} value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} data-testid="variant-weight" /></div>
              <label className="flex items-end gap-2 pb-2 cursor-pointer">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4 accent-[#2D5A4C]" data-testid="variant-active" />
                <span className="text-sm">Active</span>
              </label>
            </div>
            <div>
              <Label>Snapshot JSON</Label>
              <Textarea rows={7} value={form.snapshot_text} onChange={(e) => setForm({ ...form, snapshot_text: e.target.value })} placeholder='{"title": "…", "highlight": "…"}' className="font-mono text-xs" data-testid="variant-snapshot" />
              <p className="text-[10px] text-[#5E6D66] italic mt-1">Objet JSON avec les clés à surcharger dans la landing (title, highlight, hero, cta…).</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button className="bg-[#2D5A4C] hover:bg-[#1E4136]" onClick={save} disabled={saving} data-testid="variant-save">
              {saving ? <Loader2 className="animate-spin mr-1" size={14} /> : null}
              {editing ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={analysisOpen} onOpenChange={setAnalysisOpen}>
        <DialogContent className="max-w-2xl" data-testid="analysis-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkle size={18} className="text-[#D5007D]" weight="fill" />
              Analyse comparative A/B — IA
            </DialogTitle>
          </DialogHeader>
          {analysis && (
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-[#F0F8E4] to-[#E5F5FD] border border-[#80C51F]/30 rounded-xl p-4">
                <p className="text-[10px] uppercase tracking-widest text-[#5E6D66] font-bold">Variante gagnante</p>
                <p className="text-lg font-extrabold text-[#2D5A4C] mt-1" data-testid="analysis-winner-label">{analysis.winner_label}</p>
                <p className="text-xs text-[#5E6D66] font-mono mt-0.5">id: {analysis.winner_id}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#5E6D66] font-bold mb-1">Analyse & recommandation IA</p>
                <p className="text-sm text-[#1A2421] leading-relaxed bg-[#F5F4EF] rounded-lg p-3" data-testid="analysis-rationale">
                  {analysis.rationale || '—'}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#5E6D66] font-bold mb-1">Fusion proposée — Snapshot JSON</p>
                <pre className="text-[11px] font-mono bg-[#1A2421] text-emerald-200 rounded-lg p-3 overflow-auto max-h-64" data-testid="analysis-snapshot">
{JSON.stringify(analysis.merged_snapshot || {}, null, 2)}
                </pre>
              </div>
              <div className="text-[10px] text-[#5E6D66] italic" data-testid="analysis-generation-source">
                {analysis.ai ? '✨ Analyse générée par l’assistance IA.' : '📊 Sélection statistique (assistance IA indisponible).'}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="ghost" onClick={() => setAnalysisOpen(false)}>Fermer</Button>
            <Button
              variant="outline"
              disabled={analyzing}
              onClick={() => runAnalysis(true)}
              data-testid="analysis-create-btn"
            >
              {analyzing ? <Loader2 className="animate-spin mr-1" size={14} /> : <Plus size={14} className="mr-1" />}
              Créer comme variante inactive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
